
import { TeamRecord } from '@/types/teamTypes';
import { TableStatsProps } from '../types/tableTypes';
import { createInitialRecord, updateTeamStats } from './halfTimeUtils';

export const calculateLeagueTableStats = ({
  matches,
  viewType,
  timeType,
  pointsDeductions = []
}: TableStatsProps): TeamRecord[] => {
  if (!matches || matches.length === 0) {
    console.log('No matches provided to calculateLeagueTableStats');
    return [];
  }

  const teamStats = new Map<string, TeamRecord>();

  try {
    // Initialize teams
    matches.forEach(match => {
      if (!match.HomeTeam || !match.AwayTeam) {
        console.warn('Invalid match data:', match);
        return;
      }

      if (!teamStats.has(match.HomeTeam)) {
        teamStats.set(match.HomeTeam, createInitialRecord(match.HomeTeam));
      }
      if (!teamStats.has(match.AwayTeam)) {
        teamStats.set(match.AwayTeam, createInitialRecord(match.AwayTeam));
      }
    });

    // Process matches
    matches.forEach(match => {
      if (!match.HomeTeam || !match.AwayTeam) return;

      const homeTeam = match.HomeTeam;
      const awayTeam = match.AwayTeam;

      // Calculate match data first
      const homeGoals = timeType === 'fullTime' ? 
        (parseInt(match.FTHG) || 0) : 
        (parseInt(match.HTHG) || 0);
      const awayGoals = timeType === 'fullTime' ? 
        (parseInt(match.FTAG) || 0) : 
        (parseInt(match.HTAG) || 0);
      const result = timeType === 'fullTime' ? match.FTR : match.HTR;

      // Filter matches based on view type
      if (viewType === 'home') {
        // Only process home matches - update only the home team's record
        const homeRecord = teamStats.get(homeTeam)!;
        teamStats.set(homeTeam, updateTeamStats({
          team: homeTeam,
          homeGoals,
          awayGoals,
          result,
          isHome: true,
          record: homeRecord
        }));
        return; // Skip processing away team for home-only view
      }
      
      if (viewType === 'away') {
        // Only process away matches - update only the away team's record
        const awayRecord = teamStats.get(awayTeam)!;
        teamStats.set(awayTeam, updateTeamStats({
          team: awayTeam,
          homeGoals,
          awayGoals,
          result,
          isHome: false,
          record: awayRecord
        }));
        return; // Skip processing home team for away-only view
      }

      const homeRecord = teamStats.get(homeTeam)!;
      const awayRecord = teamStats.get(awayTeam)!;

      teamStats.set(homeTeam, updateTeamStats({
        team: homeTeam,
        homeGoals,
        awayGoals,
        result,
        isHome: true,
        record: homeRecord
      }));

      teamStats.set(awayTeam, updateTeamStats({
        team: awayTeam,
        homeGoals,
        awayGoals,
        result,
        isHome: false,
        record: awayRecord
      }));
    });

    // Apply points deductions
    pointsDeductions.forEach(deduction => {
      const team = teamStats.get(deduction.team);
      if (team) {
        team.points -= deduction.points;
        team.ppg = team.points / team.played;
      }
    });

    // Sort teams
    return Array.from(teamStats.values()).sort((a, b) => 
      b.points - a.points || 
      b.goalDifference - a.goalDifference || 
      b.goalsFor - a.goalsFor
    );
  } catch (error) {
    console.error('Error calculating league table stats:', error);
    return [];
  }
};
